import {
  Button,
  Card,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  Spinner,
  Text,
  Toolbar,
  ToolbarGroup,
} from '@fluentui/react-components';
import { Add16Regular, ArrowLeft16Regular } from '@fluentui/react-icons';
import { useCallback, useMemo } from 'react';
import { useBlocker } from 'react-router';

import { useDataHubClient } from '../../../../../../context/DataHubClientContext.js';
import { useAuth } from '../../../../../../hooks/useAuth/useAuth.js';
import { DataHubService } from '../../../../../../services/dataHubService.js';
import { TelemetryPanel } from '../../../../../../telemetry/components/TelemetryPanel/TelemetryPanel.js';
import { useUserBi } from '../../../../../../telemetry/hooks/useUserBi/useUserBi.js';
import {
  ActionScenario,
  ActionScenarioType,
  ActionOutcome,
  PanelRegion,
  PanelType,
} from '../../../../../../telemetry/userbi.js';
import { getAnnotationType } from '../../annotationTypes/registry.js';
import { computeContentHash } from '../../expectedAction/expectedActionPayload.js';
import type { LabeledBy } from '../../expectedAction/expectedActionPayload.js';
import { useAnnotationSession } from '../../hooks/useAnnotationSession.js';
import type { TranscriptTurn } from '../../utils/parseTranscript.js';
import { AnnotationConfirmationActions } from '../AnnotationConfirmationActions/AnnotationConfirmationActions.js';
import { ConversationViewer } from '../ConversationViewer/ConversationViewer.js';

import type { ConversationExpectedActionsProps } from './ConversationExpectedActions.interface.js';
import { useStyles } from './ConversationExpectedActions.style.js';

export function ConversationExpectedActions(
  props: ConversationExpectedActionsProps,
) {
  const annotationType = getAnnotationType(props.target.kind);
  if (annotationType === undefined)
    return (
      <MessageBar intent="error">
        <MessageBarBody>This schema has no registered editor.</MessageBarBody>
      </MessageBar>
    );
  return (
    <AnnotationConversation
      key={`${props.target.documentId}:${props.target.draftVersion}`}
      {...props}
    />
  );
}

function AnnotationConversation({
  conversation,
  target,
  isScrollable = false,
  fillHeight = false,
  heading,
  onSaved,
  onBack,
}: ConversationExpectedActionsProps) {
  const styles = useStyles();
  const { recordAction } = useUserBi();
  const { account } = useAuth();
  const client = useDataHubClient();
  const service = useMemo(() => new DataHubService(client), [client]);
  const annotationType = getAnnotationType(target.kind)!;
  const labeledBy = useMemo<LabeledBy | undefined>(
    () =>
      account?.localAccountId === undefined
        ? undefined
        : { id: account.localAccountId, alias: account.username },
    [account],
  );
  const contentHash = useCallback(
    () => computeContentHash(JSON.stringify(conversation)),
    [conversation],
  );
  const session = useAnnotationSession(
    service,
    target,
    annotationType,
    contentHash,
    labeledBy,
    (hasAnnotations) => onSaved?.(target.sourceFileId, hasAnnotations),
  );
  const blocker = useBlocker(session.shouldBlock);
  const blocked =
    session.isLoading ||
    session.loadError !== null ||
    session.isSaving ||
    session.needsRecovery;
  const instances = session.state.instances;

  const recordSave = () =>
    recordAction({
      panelType: PanelType.AnnotationEditorPage,
      panelRegion: PanelRegion.Main,
      actionScenario: ActionScenario.AddExpectedAction,
      actionScenarioType: ActionScenarioType.Save,
      actionOutcome: ActionOutcome.Requested,
    });
  const handleDone = (instanceId: string, draft: unknown) => {
    if (blocked) return;
    const next = instances.map((instance) =>
      instance.instanceId === instanceId
        ? { ...instance, draft, expanded: false }
        : instance,
    );
    const revision = session.replace(next, true);
    recordSave();
    session.save(next, revision);
  };
  const remove = (instanceId: string) => {
    if (blocked) return;
    const removed = instances.find(
      (instance) => instance.instanceId === instanceId,
    );
    const next = instances.filter(
      (instance) => instance.instanceId !== instanceId,
    );
    const changed = removed?.draft != null;
    const revision = session.replace(next, changed);
    if (changed) {
      recordSave();
      session.save(next, revision);
    }
  };
  const cancel = (instanceId: string) => {
    session.replace(
      instances.flatMap((instance) => {
        if (instance.instanceId !== instanceId) return [instance];
        return instance.draft === null
          ? []
          : [{ ...instance, expanded: false }];
      }),
      false,
    );
  };
  const renderTurnAction = (turn: TranscriptTurn, index: number) => (
    <Button
      appearance="subtle"
      size="small"
      icon={<Add16Regular />}
      className={styles.addButton}
      disabled={blocked}
      onClick={() => {
        recordAction({
          panelType: PanelType.AnnotationEditorPage,
          panelRegion: PanelRegion.Main,
          actionScenario: ActionScenario.AddExpectedAction,
          actionScenarioType: ActionScenarioType.Open,
        });
        session.replace(
          [
            ...instances,
            {
              instanceId: crypto.randomUUID(),
              turnId: turn.id,
              afterEventIndex: index,
              draft: null,
              expanded: true,
            },
          ],
          false,
        );
      }}
    >
      {annotationType.addActionLabel}
    </Button>
  );
  const renderTurnAttachment = (turn: TranscriptTurn) =>
    instances
      .filter((instance) => instance.turnId === turn.id)
      .map((instance) =>
        instance.draft !== null && !instance.expanded ? (
          <annotationType.SummaryComponent
            key={instance.instanceId}
            draft={instance.draft}
            onEdit={() => {
              if (!blocked)
                session.replace(
                  instances.map((value) =>
                    value.instanceId === instance.instanceId
                      ? { ...value, expanded: true }
                      : value,
                  ),
                  false,
                );
            }}
            onDelete={() => remove(instance.instanceId)}
          />
        ) : (
          <annotationType.FormComponent
            key={instance.instanceId}
            initialValue={instance.draft ?? undefined}
            disabled={blocked}
            onDone={(draft) => handleDone(instance.instanceId, draft)}
            onCancel={() => cancel(instance.instanceId)}
            onDelete={
              instance.draft === null
                ? undefined
                : () => remove(instance.instanceId)
            }
          />
        ),
      );

  return (
    <Card className={styles.root}>
      <Toolbar
        className={styles.toolbar}
        size="small"
        aria-label="Annotation document actions"
      >
        {onBack !== undefined && (
          <ToolbarGroup>
            <Button
              appearance="subtle"
              size="small"
              icon={<ArrowLeft16Regular />}
              aria-label="Back to annotation setup"
              onClick={onBack}
            >
              Back
            </Button>
          </ToolbarGroup>
        )}
        <Text weight="semibold" className={styles.documentTitle}>
          {target.fileName}
        </Text>
        <ToolbarGroup className={styles.actions}>
          {!session.isLoading &&
            !session.isSaving &&
            session.loadError === null && (
              <Text size={200} role="status" className={styles.status}>
                {session.dirty ? 'Unsaved changes' : 'Saved in Draft'}
              </Text>
            )}
          {session.isSaving && (
            <Spinner label="Saving Draft file" size="tiny" />
          )}
        </ToolbarGroup>
      </Toolbar>
      <Text size={200} className={styles.description}>
        {annotationType.label} · Draft {target.draftVersion}. Done and Delete
        save this file automatically, without publishing.
      </Text>
      {session.loadError !== null ? (
        <MessageBar intent="error" role="alert" layout="multiline">
          <MessageBarBody>
            {session.loadError.message}
            <Button
              onClick={() => {
                void session.refetch();
              }}
            >
              Retry load
            </Button>
          </MessageBarBody>
        </MessageBar>
      ) : null}
      {session.error !== null ? (
        <MessageBar intent="error" role="alert" layout="multiline">
          <MessageBarBody>
            {session.error.message}
            <Button
              disabled={session.isSaving}
              onClick={() =>
                session.needsRecovery ? session.reconcile() : session.save()
              }
            >
              {session.needsRecovery
                ? 'Reconcile save / retry cleanup'
                : 'Retry save'}
            </Button>
          </MessageBarBody>
        </MessageBar>
      ) : null}
      {session.isLoading && (
        <Spinner label="Loading selected annotation document" />
      )}
      {session.loadError === null && !session.isLoading && (
        <ConversationViewer
          conversation={conversation}
          isScrollable={isScrollable}
          fillHeight={fillHeight}
          heading={heading}
          renderTurnAction={renderTurnAction}
          renderTurnAttachment={renderTurnAttachment}
        />
      )}
      <Dialog open={blocker.state === 'blocked'}>
        <DialogSurface>
          {blocker.state === 'blocked' && (
            <TelemetryPanel
              panelType={PanelType.AnnotationEditorPage}
              panelRegion={PanelRegion.Overlay}
              eventID="annotation_editor_leave_confirmation"
            >
              <DialogBody>
                <DialogTitle>Leave annotation work?</DialogTitle>
                <DialogContent>
                  {session.isSaving
                    ? 'Wait for the current save to finish.'
                    : 'Unconfirmed forms, unsaved changes, or an unresolved save remain. Leaving discards local work; TEDH files are retained.'}
                </DialogContent>
                <AnnotationConfirmationActions
                  actionScenario={ActionScenario.AddExpectedAction}
                  pending={false}
                  confirmDisabled={session.isSaving}
                  cancelLabel="Keep editing"
                  confirmLabel="Leave"
                  onCancel={() =>
                    blocker.state === 'blocked' && blocker.reset()
                  }
                  onConfirm={() =>
                    blocker.state === 'blocked' && blocker.proceed()
                  }
                />
              </DialogBody>
            </TelemetryPanel>
          )}
        </DialogSurface>
      </Dialog>
    </Card>
  );
}
